import { useState, useCallback, useEffect } from "react";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import PortalPopup from "../../components/PortalPopup";
import ExportModalImage from "../../components/ExportModalImage";
import { useNavigate } from "react-router-dom";
import NavigationPanel from "../../components/NavigationPanel";
import { aiService } from "../../services/aiService";
import { userService } from "../../services/userService";
import DownloadModalTables from "../../components/DownloadModalTables";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import axios from "axios";
import Footer from "../../components/Footer.js";

const AI_Indicators_1 = () => {
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isExportModalImageOpen, setExportModalImageOpen] = useState(false);
  const navigate = useNavigate();

  const [summaryData, setSummaryData] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [allEntries, setAllEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Daily");
  const [currentUser, setCurrentUser] = useState(null);
  const [pendingEntriesData, setPendingEntriesData] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);
  const [timePeriod, setTimePeriod] = useState("6months");

  const [searchTerm, setSearchTerm] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] = useState(false);

  const calculateRiskFromAmount = (amount) => {
    const numAmount = Number(amount) || 0;
    if (numAmount >= 10000) return "High";
    if (numAmount >= 5000) return "Medium";
    return "Low";
  };

  const calculateAIScore = (riskLevel, amount, status) => {
    let baseScore = 50;
    if (riskLevel === "High" || riskLevel === "high") baseScore += 30;
    else if (riskLevel === "Medium" || riskLevel === "medium") baseScore += 15;
    else if (riskLevel === "Low" || riskLevel === "low") baseScore += 5;
    const amountNum = Number(amount) || 0;
    if (amountNum > 10000) baseScore += 10;
    else if (amountNum > 5000) baseScore += 5;
    if (status === "Resolved" || status === "resolved") baseScore -= 20;
    if (status === "Pending" || status === "pending") baseScore += 5;
    return Math.min(100, Math.max(0, Math.round(baseScore)));
  };

  const detectEntryType = (entry) => {
    if (entry.entryType) return entry.entryType;
    if (entry.type) return entry.type;
    if (entry.autoIncrementAccount && entry.autoIncrementAccount.startsWith("D")) return "lba";
    if (entry.originalData && entry.originalData.entryType) return entry.originalData.entryType;
    if (entry.businessName || entry.name) return "lba";
    return "unknown";
  };

  const fetchPendingEntries = async () => {
    try {
      setLoadingPending(true);
      const userId = currentUser?.id || currentUser?.userId;
      if (!userId) return;
      const pendingData = await aiService.getPendingEntries(userId);
      setPendingEntriesData(pendingData);
    } catch (error) {
      console.error("Error fetching pending entries:", error);
    } finally {
      setLoadingPending(false);
    }
  };

  useEffect(() => {
    if (currentUser) fetchPendingEntries();
  }, [currentUser]);

  const calculateMetrics = () => {
    if (!pendingEntriesData) {
      return {
        predictedRecoveryRate: 0,
        highRiskDebtors: 0,
        lowRiskDebtors: 0,
        mediumRiskDebtors: 0,
        totalEntries: 0,
        riskDistribution: [
          { name: "High risks", value: 0, count: 0 },
          { name: "Medium risks", value: 0, count: 0 },
          { name: "Low risks", value: 0, count: 0 },
        ],
      };
    }

    const { pendingEntries } = pendingEntriesData;
    const riskCounts = { high: 0, medium: 0, low: 0 };

    pendingEntries.forEach((entry) => {
      const riskLevel = entry.riskLevel?.toLowerCase();
      if (riskLevel === "high" || riskLevel === "high risk") riskCounts.high++;
      else if (riskLevel === "medium" || riskLevel === "medium risk") riskCounts.medium++;
      else if (riskLevel === "low" || riskLevel === "low risk") riskCounts.low++;
    });

    const avgRiskScore =
      (riskCounts.high * 0.3 + riskCounts.medium * 0.6 + riskCounts.low * 0.9) /
      (pendingEntries.length || 1);
    const predictedRecoveryRate = Math.round((avgRiskScore || 0.7) * 100);

    return {
      predictedRecoveryRate,
      highRiskDebtors: riskCounts.high,
      mediumRiskDebtors: riskCounts.medium,
      lowRiskDebtors: riskCounts.low,
      totalEntries: pendingEntries.length,
      riskDistribution: [
        { name: "High risks", value: riskCounts.high, count: riskCounts.high },
        { name: "Medium risks", value: riskCounts.medium, count: riskCounts.medium },
        { name: "Low risks", value: riskCounts.low, count: riskCounts.low },
      ],
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No payment";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  const generateTableData = () => {
    if (!pendingEntriesData) return [];
    const { pendingEntries } = pendingEntriesData;
    return pendingEntries.map((entry) => {
      let normalizedRisk = entry.riskLevel;
      if (entry.riskLevel?.includes("Risk")) normalizedRisk = entry.riskLevel.replace(" Risk", "");
      const entryType = detectEntryType(entry);
      const isLPD = entryType === "lpd";
      return {
        name: entry.businessName || entry.name || "N/A",
        amount: `£${(Number(entry.amount) || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        risk: normalizedRisk,
        aiScore: calculateAIScore(normalizedRisk, entry.amount, entry.status),
        lastPayment: formatDate(entry.lastPaymentDate || entry.dueDate || entry.createdAt),
        action: isLPD ? "No details" : "See details",
        id: entry._id,
        type: entryType,
        originalData: entry,
        showDetails: !isLPD,
      };
    });
  };

  const openDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(true), []);
  const closeDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(false), []);

  const metrics = calculateMetrics();
  const allTableData = generateTableData();

  const filteredTableData = allTableData.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.amount.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRisk =
      riskFilter === "all" || item.risk?.toLowerCase() === riskFilter.toLowerCase();
    return matchesSearch && matchesRisk;
  });

  const totalPages = Math.ceil(filteredTableData.length / itemsPerPage);
  const paginatedTableData = filteredTableData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  useEffect(() => { setCurrentPage(1); }, [searchTerm, riskFilter]);

  useEffect(() => {
    const user = userService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  const generateLastNDays = (days) => {
    const result = [];
    const currentDate = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(currentDate.getDate() - i);
      const startDate = new Date(date); startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(date); endDate.setHours(23, 59, 59, 999);
      result.push({ label: date.getDate().toString(), startDate, endDate });
    }
    return result;
  };

  const generateLastNMonths = (months) => {
    const result = [];
    const currentDate = new Date();
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
      const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
      result.push({ label: monthNames[date.getMonth()], startDate, endDate });
    }
    return result;
  };

  const adjustPercentages = (high, medium, low) => {
    const total = high + medium + low;
    if (total === 100) return { high, medium, low };
    const factor = 100 / total;
    return {
      high: Math.round(high * factor),
      medium: Math.round(medium * factor),
      low: Math.round(low * factor),
    };
  };

  const getDefaultTrendData = (tp = "6months") => {
    switch (tp) {
      case "7days":
        return Array.from({ length: 7 }, (_, i) => ({
          date: (i + 1).toString(),
          high: Math.round(20 + Math.random() * 30),
          medium: Math.round(30 + Math.random() * 20),
          low: Math.round(30 + Math.random() * 20),
        }));
      case "30days":
        return Array.from({ length: 30 }, (_, i) => ({
          date: (i + 1).toString(),
          high: Math.round(25 + Math.random() * 25),
          medium: Math.round(35 + Math.random() * 15),
          low: Math.round(30 + Math.random() * 20),
        }));
      default:
        return ["Jan","Feb","Mar","Apr","May","Jun"].map((month) => ({
          date: month,
          high: Math.round(30 + Math.random() * 20),
          medium: Math.round(35 + Math.random() * 15),
          low: Math.round(35 + Math.random() * 15),
        }));
    }
  };

  const generateTrendData = (tp = "6months") => {
    if (!pendingEntriesData?.pendingEntries?.length) return getDefaultTrendData(tp);
    const { pendingEntries } = pendingEntriesData;
    let dataPoints = [];
    if (tp === "7days") dataPoints = generateLastNDays(7);
    else if (tp === "30days") dataPoints = generateLastNDays(30);
    else dataPoints = generateLastNMonths(6);

    return dataPoints.map(({ label, startDate, endDate }) => {
      const periodEntries = pendingEntries.filter((entry) => {
        if (!entry.createdAt) return false;
        const d = new Date(entry.createdAt);
        return d >= startDate && d <= endDate;
      });
      const riskCounts = { high: 0, medium: 0, low: 0 };
      periodEntries.forEach((entry) => {
        const r = entry.riskLevel?.toLowerCase();
        if (r === "high" || r === "high risk") riskCounts.high++;
        else if (r === "medium" || r === "medium risk") riskCounts.medium++;
        else if (r === "low" || r === "low risk") riskCounts.low++;
      });
      const total = periodEntries.length;
      if (total > 0) {
        const adj = adjustPercentages(
          Math.round((riskCounts.high / total) * 100),
          Math.round((riskCounts.medium / total) * 100),
          Math.round((riskCounts.low / total) * 100)
        );
        return { date: label, ...adj };
      }
      return { date: label, high: 0, medium: 0, low: 0 };
    });
  };

  const calculatePercentageChanges = () => {
    const m = calculateMetrics();
    const trendData = generateTrendData(timePeriod);
    if (trendData.length >= 2) {
      const cur = trendData[trendData.length - 1];
      const prev = trendData[trendData.length - 2];
      return {
        recoveryRateChange: m.predictedRecoveryRate > 70 ? 2 : -1,
        highRiskChange: prev.high > 0 ? Math.round(((cur.high - prev.high) / prev.high) * 100) : cur.high > 0 ? 10 : 0,
        lowRiskChange: prev.low > 0 ? Math.round(((cur.low - prev.low) / prev.low) * 100) : cur.low > 0 ? 5 : 0,
      };
    }
    return {
      recoveryRateChange: m.predictedRecoveryRate > 70 ? 2 : -1,
      highRiskChange: m.highRiskDebtors > 0 ? 5 : 0,
      lowRiskChange: m.lowRiskDebtors > 0 ? 3 : 0,
    };
  };

  const graph_data = generateTrendData(timePeriod);
  const piechart_data = metrics.riskDistribution;
  const percentageChanges = calculatePercentageChanges();
  const COLORS = ["#EF4444", "#F59E0B", "#10B981"];

  const openLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(true), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);
  const openExportModalImage = useCallback(() => setExportModalImageOpen(true), []);
  const closeExportModalImage = useCallback(() => setExportModalImageOpen(false), []);

  const navigateToAI2WithData = useCallback((rowData) => {
    navigate("/ai2", {
      state: {
        name: rowData.name,
        amount: rowData.amount,
        risk: rowData.risk,
        aiScore: rowData.aiScore,
        dueDate: rowData.lastPayment,
        id: rowData.id,
        type: rowData.type,
        originalData: rowData.originalData,
      },
    });
  }, [navigate]);

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 14px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0F172A" }}>{d.name}</p>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748B" }}>Cases: <strong>{d.count}</strong></p>
        </div>
      );
    }
    return null;
  };

  const riskBadgeStyle = (risk) => {
    if (!risk) return {};
    const r = risk.toLowerCase();
    if (r === "high") return { background: "#FEF2F2", color: "#991B1B" };
    if (r === "medium") return { background: "#FFFBEB", color: "#92400E" };
    if (r === "low") return { background: "#F0FDF4", color: "#166534" };
    return { background: "#F1F5F9", color: "#475569" };
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Mono:wght@400;500&display=swap');

        html, body { background: rgb(241, 245, 249) !important; }

        .ai-root * { box-sizing: border-box; }
        .ai-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }

        .ai-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          position: relative;
          overflow: hidden;
        }

        .ai-metric-card {
          padding: 24px;
          cursor: default;
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        .ai-metric-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 24px rgba(0,0,0,0.09);
        }
        .ai-metric-card .accent-bar {
          position: absolute; top: 0; left: 0; right: 0;
          height: 4px; border-radius: 16px 16px 0 0;
        }

        .ai-icon-box {
          width: 40px; height: 40px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid #E2E8F0; margin-top: 8px; margin-bottom: 4px;
        }

        .ai-metric-label {
          font-size: 12px; font-weight: 600; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px;
        }

        .ai-metric-value {
          font-family: 'DM Mono', monospace;
          font-size: 28px; font-weight: 500; color: #0F172A; line-height: 1.1;
        }

        .ai-metric-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 10px; border-radius: 20px;
          font-size: 12px; font-weight: 700; margin-top: 8px;
        }
        .badge-green { background: #F0FDF4; color: #166534; }
        .badge-red   { background: #FEF2F2; color: #991B1B; }

        .ai-section-header {
          padding: 18px 24px;
          border-bottom: 1px solid #E2E8F0;
          display: flex; align-items: center; justify-content: space-between;
        }

        .ai-section-title { font-size: 16px; font-weight: 700; color: #0F172A; }

        .ai-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; background: #EFF6FF; border-radius: 20px;
          font-size: 12px; font-weight: 700; color: #2563EB;
        }

        .ai-tab-row {
          display: inline-flex; background: #F1F5F9;
          border-radius: 8px; padding: 3px; gap: 2px;
        }
        .ai-tab {
          padding: 6px 16px; border-radius: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #64748B;
          cursor: pointer; border: none; background: transparent;
          transition: background 0.15s, color 0.15s;
        }
        .ai-tab:hover { background: #ffffff; color: #0F172A; }
        .ai-tab.ai-tab-active {
          background: #ffffff; color: #2563EB;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
        }

        .ai-filter-row {
          padding: 12px 24px;
          border-bottom: 1px solid #F1F5F9;
          display: flex; align-items: center; justify-content: space-between;
          background: #FAFBFD;
        }

        .ai-search {
          display: flex; align-items: center; gap: 8px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px;
          padding: 8px 14px; width: 260px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .ai-search:focus-within {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .ai-search input {
          border: none; outline: none; background: transparent;
          font-family: 'DM Sans', sans-serif; font-size: 13px; color: #0F172A; width: 100%;
        }
        .ai-search input::placeholder { color: #94A3B8; }

        .ai-dl-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 14px; background: #ffffff;
          border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #1E293B;
          cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .ai-dl-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }

        .ai-table { width: 100%; border-collapse: collapse; }
        .ai-table thead tr { background: #F8FAFC; }
        .ai-table th {
          padding: 11px 16px; text-align: left;
          font-size: 11px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.07em;
          border-bottom: 1px solid #E2E8F0;
        }
        .ai-table td {
          padding: 13px 16px; font-size: 13px; color: #1E293B;
          border-bottom: 1px solid #F1F5F9; vertical-align: middle;
        }
        .ai-table tbody tr { transition: background 0.12s; }
        .ai-table tbody tr:hover { background: #F8FAFC; }
        .ai-table tbody tr:last-child td { border-bottom: none; }

        .ai-name-cell   { font-weight: 600; color: #0F172A !important; }
        .ai-amount-cell { font-family: 'DM Mono', monospace; font-size: 13px; }

        .ai-risk-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; border-radius: 20px;
          font-size: 12px; font-weight: 600;
        }

        .ai-action-link {
          color: #2563EB; font-weight: 600; font-size: 13px;
          cursor: pointer; text-decoration: none;
        }
        .ai-action-link:hover { text-decoration: underline; }
        .ai-action-disabled { color: #94A3B8; font-size: 13px; font-weight: 600; cursor: not-allowed; }

        .ai-page-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 16px; background: #ffffff;
          border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .ai-page-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .ai-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .ai-page-footer {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 24px; border-top: 1px solid #E2E8F0; background: #FAFBFD;
        }

        .ai-chart-card { padding: 24px; }
        .ai-chart-header {
          padding-bottom: 16px; border-bottom: 1px solid #E2E8F0; margin-bottom: 16px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .ai-chart-title { font-size: 15px; font-weight: 700; color: #0F172A; }
        .ai-legend-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 6px; }
      `}</style>

      <div
        className="ai-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div style={{ position: "absolute", top: 136, width: "100%", padding: "0 96px 64px", display: "flex", flexDirection: "column", gap: 28 }}>

          {/* Page header */}
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>
              AI Indicators
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
              Risk analysis and recovery predictions for pending cases.
            </p>
          </div>

          {/* Metric cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>

            {/* Predicted Recovery Rate */}
            <div className="ai-card ai-metric-card">
              <div className="accent-bar" style={{ background: "#7C3AED" }} />
              <div className="ai-icon-box" style={{ background: "#F5F3FF" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 20V10M18 20V4M6 20v-4" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="ai-metric-label">Predicted recovery rate</div>
              <div className="ai-metric-value">{metrics.predictedRecoveryRate}%</div>
              <div>
                <span className={`ai-metric-badge ${percentageChanges.recoveryRateChange >= 0 ? "badge-green" : "badge-red"}`}>
                  {percentageChanges.recoveryRateChange >= 0 ? "↑" : "↓"} {Math.abs(percentageChanges.recoveryRateChange)}%
                </span>
              </div>
            </div>

            {/* High Risk Debtors */}
            <div className="ai-card ai-metric-card">
              <div className="accent-bar" style={{ background: "#DC2626" }} />
              <div className="ai-icon-box" style={{ background: "#FEF2F2" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 9v5M12 21H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12Z" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.995 17h.009" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="ai-metric-label">High risk debtors</div>
              <div className="ai-metric-value">{metrics.highRiskDebtors}</div>
              <div>
                <span className="ai-metric-badge badge-red">
                  ↑ {Math.abs(percentageChanges.highRiskChange)}%
                </span>
              </div>
            </div>

            {/* Low Risk Debtors */}
            <div className="ai-card ai-metric-card">
              <div className="accent-bar" style={{ background: "#059669" }} />
              <div className="ai-icon-box" style={{ background: "#ECFDF5" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="m7.75 12 2.83 2.83 5.67-5.66" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="ai-metric-label">Low risk debtors</div>
              <div className="ai-metric-value">{metrics.lowRiskDebtors}</div>
              <div>
                <span className="ai-metric-badge badge-green">
                  ↑ {Math.abs(percentageChanges.lowRiskChange)}%
                </span>
              </div>
            </div>
          </div>

          {/* Charts row */}
          <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 18 }}>

            {/* Pie Chart — Risk Distribution */}
            <div className="ai-card ai-chart-card">
              <div className="ai-chart-header">
                <span className="ai-chart-title">Risk distribution</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <ResponsiveContainer width={220} height={220}>
                  <PieChart>
                    <Pie
                      data={piechart_data}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {piechart_data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>

                <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                  {[
                    { label: "High risks", color: "#EF4444", count: metrics.highRiskDebtors },
                    { label: "Medium risks", color: "#F59E0B", count: metrics.mediumRiskDebtors },
                    { label: "Low risks", color: "#10B981", count: metrics.lowRiskDebtors },
                  ].map((item) => (
                    <div key={item.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", fontSize: 13, color: "#1E293B" }}>
                        <span className="ai-legend-dot" style={{ background: item.color }} />
                        {item.label}
                      </div>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 500, color: "#0F172A" }}>
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Line Chart — Risk Trends */}
            <div className="ai-card ai-chart-card">
              <div className="ai-chart-header">
                <span className="ai-chart-title">Risk trends over time</span>
                <div className="ai-tab-row">
                  {[
                    { key: "7days", label: "7 days" },
                    { key: "30days", label: "30 days" },
                    { key: "6months", label: "6 months" },
                  ].map((t) => (
                    <button
                      key={t.key}
                      className={`ai-tab${timePeriod === t.key ? " ai-tab-active" : ""}`}
                      onClick={() => setTimePeriod(t.key)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={graph_data} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#94A3B8", fontFamily: "DM Sans" }}
                      stroke="#E2E8F0"
                      label={{
                        value: timePeriod === "6months" ? "Month" : "Day",
                        position: "insideBottom",
                        offset: -10,
                        fontSize: 11,
                        fill: "#94A3B8",
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#94A3B8", fontFamily: "DM Sans" }}
                      stroke="#E2E8F0"
                      label={{
                        value: "% of accounts",
                        angle: -90,
                        position: "insideLeft",
                        fontSize: 11,
                        fill: "#94A3B8",
                      }}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #E2E8F0",
                        borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        fontFamily: "DM Sans",
                        fontSize: 12,
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontFamily: "DM Sans", fontSize: 12, paddingTop: 8 }}
                    />
                    <Line type="monotone" dataKey="high" stroke="#EF4444" strokeWidth={2} dot={false} activeDot={{ r: 5 }} name="High risk" />
                    <Line type="monotone" dataKey="medium" stroke="#F59E0B" strokeWidth={2} dot={false} activeDot={{ r: 5 }} name="Medium risk" />
                    <Line type="monotone" dataKey="low" stroke="#10B981" strokeWidth={2} dot={false} activeDot={{ r: 5 }} name="Low risk" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Ongoing Cases Table */}
          <div className="ai-card">

            {/* Table header */}
            <div className="ai-section-header">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="ai-section-title">Ongoing cases</span>
                <span className="ai-count-pill">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563EB", display: "inline-block" }} />
                  {filteredTableData.length} {filteredTableData.length === 1 ? "entry" : "entries"}
                </span>
              </div>
              <button className="ai-dl-btn" onClick={openDownloadModalTables}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Download
              </button>
            </div>

            {/* Filter row */}
            <div className="ai-filter-row">
              <div className="ai-tab-row">
                {[
                  { key: "all", label: "View all" },
                  { key: "low", label: "Low" },
                  { key: "medium", label: "Medium" },
                  { key: "high", label: "High" },
                ].map((t) => (
                  <button
                    key={t.key}
                    className={`ai-tab${riskFilter === t.key ? " ai-tab-active" : ""}`}
                    onClick={() => setRiskFilter(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <div className="ai-search">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search cases…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button className="ai-dl-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M3 7h18M6 12h12M10 17h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Filter
                </button>
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table className="ai-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Amount</th>
                    <th>Risk level</th>
                    <th>AI score</th>
                    <th>Due date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTableData.length > 0 ? (
                    paginatedTableData.map((item, index) => (
                      <tr key={index}>
                        <td className="ai-name-cell">{item.name}</td>
                        <td className="ai-amount-cell">{item.amount}</td>
                        <td>
                          <span className="ai-risk-badge" style={riskBadgeStyle(item.risk)}>
                            ● {item.risk}
                          </span>
                        </td>
                        <td style={{ fontFamily: "'DM Mono', monospace" }}>{item.aiScore}</td>
                        <td style={{ color: "#64748B", fontSize: 13 }}>{item.lastPayment}</td>
                        <td>
                          {item.showDetails ? (
                            <a
                              className="ai-action-link"
                              onClick={(e) => { e.preventDefault(); navigateToAI2WithData(item); }}
                              href="#"
                            >
                              {item.action}
                            </a>
                          ) : (
                            <span className="ai-action-disabled">{item.action}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center", color: "#94A3B8", padding: "40px 16px" }}>
                        No entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="ai-page-footer">
              <button
                className="ai-page-btn"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                ← Previous
              </button>
              <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>
                Page {totalPages > 0 ? currentPage : 0} of {totalPages}
              </span>
              <button
                className="ai-page-btn"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next →
              </button>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113, 113, 113, 0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isDownloadModalTablesOpen && (
        <PortalPopup overlayColor="rgba(113, 113, 113, 0.3)" placement="Centered" onOutsideClick={closeDownloadModalTables}>
          <DownloadModalTables onClose={closeDownloadModalTables} tableData={paginatedTableData} tag="ai" />
        </PortalPopup>
      )}
      {isExportModalImageOpen && (
        <PortalPopup overlayColor="rgba(113, 113, 113, 0.3)" placement="Centered" onOutsideClick={closeExportModalImage}>
          <ExportModalImage onClose={closeExportModalImage} />
        </PortalPopup>
      )}
    </>
  );
};

export default AI_Indicators_1;